// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/planning/USER_MANAGEMENT_UI_PLAN_COMPLETE.md
/**
 * Password Management Component
 * 
 * Modal for managing user passwords:
 * - Reset password
 * - Change password (admin)
 * - Generate temporary password
 * - Password strength validation
 * - Account unlock
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  LinearProgress
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// Import service
import userManagementService from '../../services/userManagementService';

const PasswordManagement = ({ open, onClose, user, onPasswordChanged, isSelfPasswordChange = false }) => {
  const [formData, setFormData] = useState({
    oldPassword: '', // For self-password change
    newPassword: '',
    confirmPassword: '',
    action: 'change', // change, reset, generate
    temporaryPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Reset form when dialog opens for self-password change
  React.useEffect(() => {
    if (open && isSelfPasswordChange) {
      setFormData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
        action: 'change', // Always use 'change' for self-password changes
        temporaryPassword: ''
      });
      setErrors({});
      setPasswordStrength(0);
    }
  }, [open, isSelfPasswordChange]);

  // Password validation function matching backend requirements
  const validatePasswordStrength = (password) => {
    const errors = [];
    
    // Check minimum length
    if (password.length < 8) {
      errors.push("Password must be at least 8 characters long");
    }
    
    // Check for uppercase letter
    if (!/[A-Z]/.test(password)) {
      errors.push("Password must contain at least one uppercase letter");
    }
    
    // Check for lowercase letter
    if (!/[a-z]/.test(password)) {
      errors.push("Password must contain at least one lowercase letter");
    }
    
    // Check for number
    if (!/\d/.test(password)) {
      errors.push("Password must contain at least one number");
    }
    
    // Check for special character
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push("Password must contain at least one special character (!@#$%^&*...)");
    }
    
    return errors;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }

    // Calculate password strength
    if (field === 'newPassword') {
      const strength = calculatePasswordStrength(value);
      setPasswordStrength(strength);
    }
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    
    if (password.length >= 8) strength += 20;
    if (password.length >= 12) strength += 20;
    if (/[A-Z]/.test(password)) strength += 20;
    if (/[a-z]/.test(password)) strength += 20;
    if (/[0-9]/.test(password)) strength += 10;
    if (/[^A-Za-z0-9]/.test(password)) strength += 10;
    
    return Math.min(strength, 100);
  };

  const getPasswordStrengthColor = (strength) => {
    if (strength < 30) return 'error';
    if (strength < 60) return 'warning';
    if (strength < 80) return 'info';
    return 'success';
  };

  const getPasswordStrengthText = (strength) => {
    if (strength < 30) return 'Weak';
    if (strength < 60) return 'Fair';
    if (strength < 80) return 'Good';
    return 'Strong';
  };

  const generateTemporaryPassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, temporaryPassword: password }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.action === 'change' || formData.action === 'reset') {
      // For self-password change, validate old password
      if (isSelfPasswordChange && !formData.oldPassword) {
        newErrors.oldPassword = 'Current password is required';
      }

      if (!formData.newPassword) {
        newErrors.newPassword = 'Password is required';
      } else {
        // Validate password strength according to backend requirements
        const passwordErrors = validatePasswordStrength(formData.newPassword);
        if (passwordErrors.length > 0) {
          newErrors.newPassword = passwordErrors.join(', ');
        }
      }

      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      if (formData.action === 'change') {
        if (isSelfPasswordChange) {
          // User changing their own password (requires old password)
          await userManagementService.user.changePassword(
            user.id,
            {
              old_password: formData.oldPassword,
              new_password: formData.newPassword
            }
          );
        } else {
          // Admin setting specific password (doesn't require old password)
          await userManagementService.user.adminChangePassword(
            user.id,
            formData.newPassword,
            false  // User doesn't need to change on next login
          );
        }
      } else if (formData.action === 'reset') {
        // Admin setting specific password + force change on next login
        await userManagementService.user.adminChangePassword(
          user.id,
          formData.newPassword,
          true  // User must change on next login
        );
      } else if (formData.action === 'generate') {
        // Generate temp password (backend generates it)
        await userManagementService.user.resetPassword(
          user.id,
          true  // Force change on next login
        );
      }

      onPasswordChanged();
    } catch (error) {
      console.error('Failed to update password:', error);
      setErrors({ general: error.response?.data?.detail || 'Failed to update password. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      newPassword: '',
      confirmPassword: '',
      action: 'change',
      temporaryPassword: ''
    });
    setErrors({});
    setPasswordStrength(0);
    onClose();
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ 
        bgcolor: 'primary.main', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <LockIcon />
        {isSelfPasswordChange ? 'Change My Password' : `Password Management - ${user.full_name || user.nickname || user.username}`}
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 3, pb: 2 }}>
          {errors.general && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.general}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Action Selection */}
            {/* Password Action - Only show for admin password management */}
            {!isSelfPasswordChange && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ 
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2
                }}>
                  <SecurityIcon />
                  Password Action
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={formData.action || ''}
                    label="Action"
                    onChange={(e) => handleInputChange('action', e.target.value)}
                  >
                    <MenuItem value="change">Change Password</MenuItem>
                    <MenuItem value="reset">Reset Password (User must change on next login)</MenuItem>
                    <MenuItem value="generate">Generate Temporary Password</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Password Fields */}
            {(isSelfPasswordChange || formData.action === 'change' || formData.action === 'reset') && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Current Password - Full Width */}
                    {isSelfPasswordChange && (
                      <TextField
                        fullWidth
                        label="Current Password"
                        placeholder="Enter current password"
                        type={showOldPassword ? 'text' : 'password'}
                        value={formData.oldPassword}
                        onChange={(e) => handleInputChange('oldPassword', e.target.value)}
                        error={!!errors.oldPassword}
                        helperText={errors.oldPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowOldPassword(!showOldPassword)}
                                edge="end"
                              >
                                {showOldPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    
                    {/* New Password and Confirm Password - Side by Side */}
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          fullWidth
                          label={isSelfPasswordChange ? "New Password" : "Enter new password"}
                          placeholder="Enter new password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.newPassword}
                          onChange={(e) => handleInputChange('newPassword', e.target.value)}
                          error={!!errors.newPassword}
                          helperText={errors.newPassword}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                >
                                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          fullWidth
                          label="Confirm new password"
                          placeholder="Confirm new password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={formData.confirmPassword}
                          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                          error={!!errors.confirmPassword}
                          helperText={errors.confirmPassword}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                  edge="end"
                                >
                                  {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </Box>
                    
                    {/* Password Requirements - Full Width Below */}
                    {formData.newPassword && (
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>
                          Password Requirements:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.875rem' }}>
                          <Box component="li" sx={{ color: formData.newPassword.length >= 8 ? 'success.main' : 'error.main' }}>
                            At least 8 characters
                          </Box>
                          <Box component="li" sx={{ color: /[A-Z]/.test(formData.newPassword) ? 'success.main' : 'error.main' }}>
                            At least one uppercase letter
                          </Box>
                          <Box component="li" sx={{ color: /[a-z]/.test(formData.newPassword) ? 'success.main' : 'error.main' }}>
                            At least one lowercase letter
                          </Box>
                          <Box component="li" sx={{ color: /\d/.test(formData.newPassword) ? 'success.main' : 'error.main' }}>
                            At least one number
                          </Box>
                          <Box component="li" sx={{ color: /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword) ? 'success.main' : 'error.main' }}>
                            At least one special character (!@#$%^&*...)
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Password Strength Indicator */}
                {formData.newPassword && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: -1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption">Password Strength</Typography>
                        <Typography 
                          variant="caption" 
                          color={`${getPasswordStrengthColor(passwordStrength)}.main`}
                          fontWeight="bold"
                        >
                          {getPasswordStrengthText(passwordStrength)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={passwordStrength}
                        color={getPasswordStrengthColor(passwordStrength)}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  </Grid>
                )}
              </>
            )}

            {/* Temporary Password Generation */}
            {formData.action === 'generate' && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <TextField
                      fullWidth
                      placeholder="Click Generate to create temporary password"
                      value={formData.temporaryPassword}
                      InputProps={{ readOnly: true }}
                      helperText="Copy this password and share it securely with the user"
                    />
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={generateTemporaryPassword}
                      sx={{ minWidth: 'auto', px: 2, py: 1.75 }}
                    >
                      Generate
                    </Button>
                  </Box>
                </Grid>
              </>
            )}

            {/* User Info */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Alert severity="info">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>User:</strong> {user.full_name || user.username} ({user.email})
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Current Role:</strong> {user.role_name || user.role || 'Unknown'}
                </Typography>
                {user.is_locked && (
                  <Typography variant="body2" color="error">
                    <strong>Status:</strong> Account is currently locked
                  </Typography>
                )}
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || (formData.action === 'generate' && !formData.temporaryPassword)}
            sx={{ 
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Updating...
              </>
            ) : (
              isSelfPasswordChange ? 'Change My Password' : 
              (formData.action === 'generate' ? 'Set Temporary Password' : 'Update Password')
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PasswordManagement;
